/**
 * Paste or drop a screenshot into a ticket composer.
 *
 * The upload returns a URL and this writes ordinary Markdown into the text the
 * user is already typing — `![name](url)`. That is the whole trick: no rich-text
 * editor, no separate attachment list to keep in sync with the body, and the
 * image lands exactly where the reporter was writing about it.
 *
 * Two modes, because the moment a screenshot matters most is while FILING a bug,
 * when there is no ticket to attach it to yet:
 *
 *   ticketId given   → POST /tickets/{id}/attachments, bound immediately
 *   ticketId absent  → POST /tickets/attachments, bound by POST /tickets via
 *                      the ids collected through `onUploaded`
 */
import { useCallback, useState } from 'react'
import api from '../lib/api'
import { apiErrorMessage } from '../lib/apiError'

export interface UploadedTicketImage {
  id: number
  url: string
  filename: string
}

interface Options {
  /** Omit while composing a ticket that does not exist yet. */
  ticketId?: number
  /** Append this Markdown to the composer's text. */
  onInsert: (markdown: string) => void
  /** Collect the id so POST /tickets can bind it. Unused in the bound mode. */
  onUploaded?: (image: UploadedTicketImage) => void
  onError: (message: string) => void
}

function imagesFrom(list: FileList | null | undefined): File[] {
  // `startsWith('image/')` is deliberately looser than the server's allow-list.
  // Filtering here decides whether to intercept the paste at all; letting an
  // SVG through to be refused with a real message beats swallowing the paste
  // and leaving the user wondering why nothing happened.
  return Array.from(list ?? []).filter((f) => f.type.startsWith('image/'))
}

export function useTicketImagePaste({ ticketId, onInsert, onUploaded, onError }: Options) {
  const [uploading, setUploading] = useState(false)

  const upload = useCallback(
    async (files: File[]) => {
      setUploading(true)
      try {
        for (const file of files) {
          const form = new FormData()
          form.append('file', file)
          // No explicit Content-Type: the browser has to set it so the multipart
          // boundary comes with it. Setting it by hand produces a request the
          // server cannot parse.
          const path = ticketId ? `/tickets/${ticketId}/attachments` : '/tickets/attachments'
          const { data } = await api.post<UploadedTicketImage>(path, form)
          onInsert(`![${data.filename}](${data.url})`)
          onUploaded?.(data)
        }
      } catch (err) {
        onError(apiErrorMessage(err))
      } finally {
        setUploading(false)
      }
    },
    [ticketId, onInsert, onUploaded, onError],
  )

  const onPaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const files = imagesFrom(event.clipboardData?.files)
      if (!files.length) return
      // Only prevented when there IS an image: a normal text paste must still
      // behave like a normal text paste.
      event.preventDefault()
      void upload(files)
    },
    [upload],
  )

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLTextAreaElement>) => {
      const files = imagesFrom(event.dataTransfer?.files)
      if (!files.length) return
      event.preventDefault()
      void upload(files)
    },
    [upload],
  )

  // Without this the browser navigates away to the dropped file, which loses
  // whatever the user had typed.
  const onDragOver = useCallback((event: React.DragEvent<HTMLTextAreaElement>) => {
    if (Array.from(event.dataTransfer?.types ?? []).includes('Files')) event.preventDefault()
  }, [])

  return { onPaste, onDrop, onDragOver, uploading }
}
